import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FailedRecording, AudioSegment, AudioSegmentMetadata } from '../interfaces/recording-storage.interface';

const DB_NAME = 'RecordingStore';
const DB_VERSION = 1;
const STORE_RECORDINGS = 'failedRecordings';
const STORE_SEGMENTS = 'recordingSegments';

/**
 * DOMException names that mean "this connection is gone" rather than "this request was
 * invalid". Browsers force-close IndexedDB connections under storage/memory pressure (and
 * when the backing store hits an IO error); once that happens the cached IDBDatabase handle
 * throws `InvalidStateError: ... The database connection is closing.` on every subsequent
 * transaction. The only fix is to drop the handle and reopen.
 */
const CONNECTION_LOST_ERRORS = ['InvalidStateError', 'AbortError', 'UnknownError'];

/**
 * Body of a transaction. Requests must be registered synchronously so the transaction does
 * not auto-commit before the work is queued.
 */
type TransactionWork<T> = (
  tx: IDBTransaction,
  resolve: (value: T) => void,
  reject: (reason: any) => void
) => void;

@Injectable({
  providedIn: 'root'
})
export class RecordingStorageService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<IDBDatabase> | null = null;

  // Observable for failed recordings list
  private failedRecordingsSubject = new BehaviorSubject<FailedRecording[]>([]);
  public failedRecordings$: Observable<FailedRecording[]> = this.failedRecordingsSubject.asObservable();

  // Debounce timer for refreshing failed recordings when segments are stored
  private refreshTimer: any = null;

  /**
   * How long a `recording` row may go without a heartbeat before its tab is presumed dead.
   *
   * A capturing tab refreshes the heartbeat as each segment is cut, so once a minute at the
   * current 60 s slice. Three minutes leaves room for a slow write or a briefly throttled
   * background tab without leaving a genuinely lost consultation hidden for long. Erring long
   * is the safe direction: promoting a live recording would show a clinician a scary
   * "unfinished consultation" for the consult they are in the middle of.
   */
  private static readonly ABANDONED_AFTER_MS = 3 * 60 * 1000;

  // Depth counter for pauseFailedRecordingsRefresh()/resumeFailedRecordingsRefresh()
  private refreshSuspendDepth = 0;
  private refreshMissedWhileSuspended = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    console.log('🎯 RecordingStorageService constructor called');
    if (isPlatformBrowser(this.platformId)) {
      console.log('✅ Platform is browser, initializing IndexedDB...');
      this.openDB().catch(err => console.error('Failed to open IndexedDB:', err));
      this.requestPersistentStorage();
    } else {
      console.warn('⚠️ Platform is not browser, skipping IndexedDB initialization');
    }
  }

  /**
   * Asks the browser not to evict this origin's storage under pressure.
   *
   * Without it the segment store is "best-effort": the browser may clear it when disk or
   * memory runs short, which is exactly the condition a long consultation creates. The one
   * durable copy of a consultation that has not finished uploading lives here.
   *
   * Advisory and unattended — Chrome grants it on an installed/engaged site and silently
   * declines otherwise, Safari does not implement it at all. Nothing downstream may assume it
   * succeeded; this only removes a failure mode that is otherwise invisible and unfixable.
   */
  private async requestPersistentStorage(): Promise<void> {
    try {
      if (!navigator.storage?.persist || !navigator.storage?.persisted) return;
      if (await navigator.storage.persisted()) return;

      const granted = await navigator.storage.persist();
      console.log(`[RecordingStorage] Persistent storage ${granted ? 'granted' : 'not granted'}`);
    } catch (error) {
      console.debug('[RecordingStorage] Persistent storage unavailable:', error);
    }
  }

  // ---------------------------------------------------------------------------------------
  // Connection management
  // ---------------------------------------------------------------------------------------

  private openDB(): Promise<IDBDatabase> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise<IDBDatabase>((resolve, reject) => {
      let request: IDBOpenDBRequest;
      try {
        request = indexedDB.open(DB_NAME, DB_VERSION);
      } catch (error) {
        this.initPromise = null;
        reject(error);
        return;
      }

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create failedRecordings object store
        if (!db.objectStoreNames.contains(STORE_RECORDINGS)) {
          const recordingsStore = db.createObjectStore(STORE_RECORDINGS, { keyPath: 'id' });
          recordingsStore.createIndex('status', 'status', { unique: false });
          recordingsStore.createIndex('patientId', 'patientId', { unique: false });
          recordingsStore.createIndex('createdAt', 'createdAt', { unique: false });
          recordingsStore.createIndex('jobId', 'jobId', { unique: true });
          recordingsStore.createIndex('patientName', 'patientName', { unique: false });
        }

        // Create recordingSegments object store
        if (!db.objectStoreNames.contains(STORE_SEGMENTS)) {
          const segmentsStore = db.createObjectStore(STORE_SEGMENTS, { keyPath: 'id' });
          segmentsStore.createIndex('recordingId', 'recordingId', { unique: false });
          segmentsStore.createIndex('order', 'order', { unique: false });
          segmentsStore.createIndex('uploadSuccess', 'uploadSuccess', { unique: false });
          segmentsStore.createIndex('processed', 'processed', { unique: false });
        }
      };

      request.onblocked = () => {
        // Another tab is holding an older version open. Its onversionchange handler (below)
        // closes it, so this normally resolves itself.
        console.warn('[RecordingStorage] IndexedDB open is blocked by another connection');
      };

      request.onsuccess = () => {
        const db = request.result;
        this.attachConnectionListeners(db);
        this.db = db;
        console.log('✅ IndexedDB initialized successfully:', DB_NAME);
        resolve(db);
        // Promote anything a dead tab left mid-recording before the first read, so the very
        // first emission already carries it and the header badge is right on load.
        // `reconcileAbandonedRecordings()` refreshes on its own when it promotes anything.
        this.reconcileAbandonedRecordings()
          .then(promoted => {
            if (promoted === 0) return this.refreshFailedRecordings();
            return undefined;
          })
          .catch(err =>
            console.error('Failed to initialize failed recordings observable:', err)
          );
      };

      request.onerror = () => {
        const error = request.error ?? new Error('Failed to open IndexedDB');
        console.error('IndexedDB error:', error);
        // Clear the cached promise so a later call can try again instead of replaying the
        // failure for the rest of the session.
        this.initPromise = null;
        this.db = null;
        reject(error);
      };
    });

    return this.initPromise;
  }

  /**
   * The browser can close a connection out from under us (force-close under storage/memory
   * pressure, another tab upgrading the schema, the backing store erroring). Without these
   * listeners the service keeps handing out a dead handle and every read/write throws
   * "The database connection is closing" until the page is reloaded.
   */
  private attachConnectionListeners(db: IDBDatabase): void {
    db.onclose = () => this.handleConnectionLost(db, 'connection closed by the browser');
    db.onversionchange = () => {
      // Let another tab's upgrade proceed instead of blocking it forever.
      try {
        db.close();
      } finally {
        this.handleConnectionLost(db, 'versionchange');
      }
    };
    db.onerror = (event) => {
      console.error('[RecordingStorage] IndexedDB connection error:', (event.target as any)?.error);
    };
  }

  private handleConnectionLost(db: IDBDatabase, reason: string): void {
    // A newer connection (or a reopen already in flight) may have replaced this one — a late
    // close event from the old handle must not tear that down.
    if (this.db !== db) {
      return;
    }
    console.warn(`[RecordingStorage] IndexedDB connection lost (${reason}); reopening on next use`);
    this.db = null;
    this.initPromise = null;
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('IndexedDB is not available outside the browser');
    }
    if (this.db) {
      return this.db;
    }
    return this.openDB();
  }

  private isConnectionLostError(error: any): boolean {
    const name = error?.name;
    if (typeof name === 'string' && CONNECTION_LOST_ERRORS.includes(name)) {
      return true;
    }
    const message = typeof error?.message === 'string' ? error.message : '';
    return /database connection is clos/i.test(message);
  }

  /**
   * Runs a transaction, and retries it once on a freshly opened connection if the failure
   * was a lost connection. Every read/write goes through here so no code path is left
   * holding a dead handle.
   */
  private async runTransaction<T>(
    storeNames: string[],
    mode: IDBTransactionMode,
    work: TransactionWork<T>
  ): Promise<T> {
    let lastError: any;

    for (let attempt = 0; attempt < 2; attempt++) {
      const db = await this.ensureDB();

      try {
        return await new Promise<T>((resolve, reject) => {
          // Throws synchronously with InvalidStateError if the connection is closing.
          const tx = db.transaction(storeNames, mode);
          tx.onabort = () => reject(tx.error ?? new DOMException('IndexedDB transaction aborted', 'AbortError'));
          tx.onerror = () => reject(tx.error ?? new DOMException('IndexedDB transaction failed', 'UnknownError'));
          work(tx, resolve, reject);
        });
      } catch (error: any) {
        lastError = error;
        if (!this.isConnectionLostError(error)) {
          throw error;
        }
        this.handleConnectionLost(db, `${error?.name}: ${error?.message}`);
        if (attempt === 0) {
          console.warn('[RecordingStorage] Retrying operation on a fresh connection');
        }
      }
    }

    throw lastError;
  }

  /**
   * Walks a cursor and projects each record, instead of using getAll().
   *
   * getAll() returns every matching record — audio payloads included — in a single response;
   * over a store holding hours of consultations that is large enough for the browser to fail
   * the read and force-close the connection. A cursor keeps one record in flight at a time,
   * and `project` drops the payload reference straight away for metadata-only reads.
   */
  private collectCursor<T>(
    request: IDBRequest<IDBCursorWithValue | null>,
    project: (value: any) => T | null,
    resolve: (value: T[]) => void,
    reject: (reason: any) => void
  ): void {
    const results: T[] = [];

    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve(results);
        return;
      }
      try {
        const projected = project(cursor.value);
        if (projected !== null) {
          results.push(projected);
        }
      } catch (error) {
        reject(error);
        return;
      }
      cursor.continue();
    };

    request.onerror = () => reject(request.error);
  }

  /** Strips the audio payload so scans never hold more than one buffer at a time. */
  private toSegmentMetadata(record: any): AudioSegmentMetadata {
    const { blob, blobData, ...metadata } = record;
    return metadata as AudioSegmentMetadata;
  }

  /**
   * iOS Safari fix: segments are stored as ArrayBuffers because Blobs put straight into
   * IndexedDB come back "dead" there. Rehydrate on the way out.
   */
  private restoreSegment(record: any): AudioSegment {
    if (record.blobData instanceof ArrayBuffer) {
      try {
        record.blob = new Blob([record.blobData], { type: record.mimeType || 'audio/webm' });
        delete record.blobData;
      } catch (error) {
        console.error(`Failed to recreate blob from ArrayBuffer for segment ${record.id}:`, error);
        // Keep blobData if recreation fails
      }
    } else if (record.blob instanceof Blob) {
      // Backward compatibility: old segments stored with the blob directly
      if (record.blob.size === 0 && record.size > 0) {
        console.warn(`Segment ${record.id} has dead blob (size mismatch). This is an old segment that needs to be re-stored.`);
      }
    }
    return record as AudioSegment;
  }

  /**
   * Approximate wall-clock duration of a set of segments from their byte sizes.
   *
   * Only reached for *orphaned* segments — ones with no parent row. A recording started since
   * the parent row began being written at `startRecording()` carries a real `totalDuration`
   * measured from the recorder's own clock, and that is always preferred over this.
   *
   * The old constant here was "1 minute ≈ 1MB (for webm/opus)" applied to every segment
   * regardless of format. Our WebKit path — which per `tasks/recording-audio-gaps.md` §1 is
   * every browser except Firefox and Opera — writes uncompressed 16-bit mono WAV at the device
   * sample rate, about 5.8 MB per minute. That constant therefore reported a five-minute
   * consultation as twenty-seven minutes, which is worse than saying nothing.
   */
  private estimateDurationSeconds(segments: AudioSegmentMetadata[]): number {
    // Bytes per second, by container. WAV is PCM so its rate is exact given the format we
    // write; the compressed formats are their configured bitrates.
    const WAV_BYTES_PER_SECOND = 48000 * 2;   // 48 kHz, 16-bit, mono
    const OPUS_BYTES_PER_SECOND = 64000 / 8;  // audioBitsPerSecond: 64000
    const AAC_BYTES_PER_SECOND = 64000 / 8;   // Android native, AAC 16 kHz

    const totalSeconds = segments.reduce((sum, seg) => {
      const size = seg.size || 0;
      if (!size) return sum;

      const mime = (seg.mimeType || '').toLowerCase();
      let bytesPerSecond = WAV_BYTES_PER_SECOND;
      if (mime.includes('opus') || mime.includes('webm') || mime.includes('ogg')) {
        bytesPerSecond = OPUS_BYTES_PER_SECOND;
      } else if (mime.includes('mp4') || mime.includes('aac') || mime.includes('m4a')) {
        bytesPerSecond = AAC_BYTES_PER_SECOND;
      }

      return sum + size / bytesPerSecond;
    }, 0);

    return Math.round(totalSeconds);
  }

  /** The scan predicate that decides what shows up in the failed recordings dialog. */
  private isFailedSegmentRecord(segment: AudioSegmentMetadata): boolean {
    return segment.uploadSuccess === false ||
      segment.transcribeFailed === true ||
      segment.processed === false;
  }

  // ---------------------------------------------------------------------------------------
  // Writes
  // ---------------------------------------------------------------------------------------

  // Store a single segment
  async storeSegment(segment: AudioSegment): Promise<string> {
    console.log('📦 RecordingStorageService.storeSegment called:', segment.id, 'for recording:', segment.recordingId);

    // iOS Safari fix: Convert Blob to ArrayBuffer for storage
    // Blobs stored directly in IndexedDB become "dead" on iOS Safari when retrieved
    // ArrayBuffers work reliably across all platforms.
    // NOTE: this must happen before the transaction is opened — awaiting inside a
    // transaction lets it auto-commit.
    const segmentForStorage: any = { ...segment };
    if (segment.blob instanceof Blob) {
      try {
        segmentForStorage.blobData = await segment.blob.arrayBuffer();
        // Remove the blob property - we'll store as ArrayBuffer instead
        delete segmentForStorage.blob;
        console.log(`Converted blob to ArrayBuffer for segment ${segment.id} (size: ${segmentForStorage.blobData.byteLength})`);
      } catch (error) {
        console.error('Failed to convert blob to ArrayBuffer, storing blob directly (may not work on iOS):', error);
        // Fallback: store blob directly (will fail on iOS)
      }
    }

    await this.runTransaction<void>([STORE_SEGMENTS], 'readwrite', (tx, resolve, reject) => {
      const request = tx.objectStore(STORE_SEGMENTS).put(segmentForStorage);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    console.log('✅ Successfully stored segment in IndexedDB:', segment.id);
    // If this is a failed segment, trigger a refresh of failed recordings (debounced)
    // This ensures orphaned failed segments show up in the header warning
    if (!segment.uploadSuccess || segment.transcribeFailed || !segment.processed) {
      this.scheduleFailedRecordingsRefresh();
    }
    return segment.id;
  }

  /**
   * Updates a segment's metadata (transcript, upload/processing flags) without rewriting the
   * audio payload through JS.
   *
   * The alternative — mutating a segment read via getRecordingSegments() and calling
   * storeSegment() — re-runs blob.arrayBuffer() on a rehydrated blob for every transcribed
   * segment, and on iOS would overwrite good audio with an empty buffer if that blob is dead.
   */
  async updateSegmentMetadata(
    segmentId: string,
    updates: Partial<Omit<AudioSegmentMetadata, 'id'>>
  ): Promise<boolean> {
    const updated = await this.runTransaction<boolean>([STORE_SEGMENTS], 'readwrite', (tx, resolve, reject) => {
      const store = tx.objectStore(STORE_SEGMENTS);
      const getRequest = store.get(segmentId);

      getRequest.onsuccess = () => {
        const record = getRequest.result;
        if (!record) {
          console.warn(`[RecordingStorage] Segment ${segmentId} not found; nothing to update`);
          resolve(false);
          return;
        }

        // Payload (blobData/blob) is carried through untouched.
        Object.assign(record, updates);

        const putRequest = store.put(record);
        putRequest.onsuccess = () => resolve(true);
        putRequest.onerror = () => reject(putRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });

    if (updated) {
      this.scheduleFailedRecordingsRefresh();
    }
    return updated;
  }

  // Store recording with metadata (segments stored separately)
  async storeRecording(recording: FailedRecording): Promise<string> {
    await this.runTransaction<void>([STORE_RECORDINGS], 'readwrite', (tx, resolve, reject) => {
      const request = tx.objectStore(STORE_RECORDINGS).put(recording);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // Debounced and suspendable, exactly like storeSegment above — NOT a direct refresh.
    //
    // The parent row is now rewritten once per slice while a consultation records, to keep its
    // segment list and heartbeat current. A direct refresh here would walk the whole segments
    // store every minute of every consultation and would do it straight through the suspension
    // that `pauseFailedRecordingsRefresh()` exists to provide. Suppressed refreshes are
    // replayed on resume, so nothing is lost by deferring.
    this.scheduleFailedRecordingsRefresh();
    return recording.id;
  }

  // Update recording status
  async updateRecordingStatus(id: string, status: string): Promise<void> {
    const recording = await this.getRecording(id);
    if (!recording) {
      throw new Error(`Recording ${id} not found`);
    }

    recording.status = status as FailedRecording['status'];
    if (status === 'completed') {
      recording.completedAt = new Date().toISOString();
    }
    if (status === 'retrying') {
      recording.lastAttempt = new Date().toISOString();
    }

    await this.storeRecording(recording);
    // refreshFailedRecordings is called by storeRecording
  }

  // Mark recording as failed with error details
  async markRecordingAsFailed(
    recordingId: string,
    errorInfo: { error: string; errorCode?: number; failedSegmentId?: string }
  ): Promise<void> {
    let recording = await this.getRecording(recordingId);

    if (!recording) {
      // Create new recording if it doesn't exist
      recording = {
        id: recordingId,
        jobId: recordingId,
        sessionId: recordingId,
        patientId: '', // Will be set when recording is finalized
        groupId: '',
        userId: '',
        type: 'general',
        segmentIds: [],
        totalDuration: 0,
        status: 'failed',
        uploadFailed: true,
        transcriptionFailed: true,
        failedSegments: errorInfo.failedSegmentId ? [errorInfo.failedSegmentId] : [],
        createdAt: new Date().toISOString(),
        version: 1
      };
    }

    recording.status = 'failed';
    recording.uploadFailed = true;
    recording.transcriptionFailed = true;
    recording.error = errorInfo.error;
    recording.errorCode = errorInfo.errorCode;
    if (errorInfo.failedSegmentId && !recording.failedSegments.includes(errorInfo.failedSegmentId)) {
      recording.failedSegments.push(errorInfo.failedSegmentId);
    }
    recording.lastAttempt = new Date().toISOString();

    await this.storeRecording(recording);
    // refreshFailedRecordings is called by storeRecording
  }

  // Delete recording and all its segments (atomically, in one transaction)
  async deleteRecording(id: string): Promise<void> {
    await this.runTransaction<void>([STORE_RECORDINGS, STORE_SEGMENTS], 'readwrite', (tx, resolve, reject) => {
      const segmentsStore = tx.objectStore(STORE_SEGMENTS);
      const cursorRequest = segmentsStore.index('recordingId').openCursor(IDBKeyRange.only(id));

      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
          return;
        }

        const deleteRequest = tx.objectStore(STORE_RECORDINGS).delete(id);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
      };

      cursorRequest.onerror = () => reject(cursorRequest.error);
    });

    // Emit updated failed recordings after deletion
    this.refreshFailedRecordings().catch(err =>
      console.error('Failed to refresh failed recordings after delete:', err)
    );
  }

  // Delete all segments for a recording
  async deleteRecordingSegments(recordingId: string): Promise<void> {
    await this.runTransaction<void>([STORE_SEGMENTS], 'readwrite', (tx, resolve, reject) => {
      const store = tx.objectStore(STORE_SEGMENTS);
      const request = store.index('recordingId').openCursor(IDBKeyRange.only(recordingId));

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // ---------------------------------------------------------------------------------------
  // Reads
  // ---------------------------------------------------------------------------------------

  // Get all failed recordings
  async getFailedRecordings(): Promise<FailedRecording[]> {
    const recordings = await this.runTransaction<FailedRecording[]>(
      [STORE_RECORDINGS],
      'readonly',
      (tx, resolve, reject) => {
        const request = tx.objectStore(STORE_RECORDINGS).openCursor();
        this.collectCursor<FailedRecording>(
          request,
          (record: FailedRecording) => {
            // `recording` is the one status deliberately excluded: a consultation being
            // captured right now is not something to offer the clinician for recovery.
            // Everything else that is not a viewed success is.
            const keep = record.status === 'failed' ||
              record.status === 'abandoned' ||
              record.status === 'pending' ||
              record.status === 'retrying' ||
              (record.status === 'completed' && !!record.finalResult);
            return keep ? record : null;
          },
          resolve,
          reject
        );
      }
    );

    // Sort by createdAt descending (newest first)
    recordings.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return recordings;
  }

  /**
   * Promotes recordings that were being captured when their tab died to `abandoned`.
   *
   * This is the recovery path that needs no cooperation from the dying page. `beforeunload`
   * and `pagehide` both assume the page gets told; a renderer kill, an OS kill or a flat
   * battery tell it nothing. A row left in `recording` is proof on its own that a
   * consultation was being captured and never finished, and it is sitting in IndexedDB
   * alongside every segment that was cut.
   *
   * Staleness, not mere presence, is the test: IndexedDB is shared across a browser's tabs,
   * so a second tab loading the app must not promote a recording that is live in the first.
   * A capturing tab refreshes `heartbeatAt` as each segment is cut — once a minute — so
   * anything untouched for {@link ABANDONED_AFTER_MS} has no tab behind it.
   *
   * Called on service init and again whenever the AI dialog or the Failed Recordings dialog
   * opens, which covers the case of a clinician reloading immediately after a crash, when the
   * heartbeat is still too fresh to judge.
   *
   * @returns how many recordings were promoted.
   */
  async reconcileAbandonedRecordings(): Promise<number> {
    const cutoff = Date.now() - RecordingStorageService.ABANDONED_AFTER_MS;

    const stale = await this.runTransaction<FailedRecording[]>(
      [STORE_RECORDINGS],
      'readonly',
      (tx, resolve, reject) => {
        const request = tx.objectStore(STORE_RECORDINGS).openCursor();
        this.collectCursor<FailedRecording>(
          request,
          (record: FailedRecording) => {
            if (record.status !== 'recording') return null;
            // No heartbeat at all means it predates this field, or the tab died before the
            // first segment was cut. `createdAt` is the next best sign of life.
            const lastSeen = Date.parse(record.heartbeatAt || record.createdAt);
            if (!Number.isFinite(lastSeen)) return record;
            return lastSeen < cutoff ? record : null;
          },
          resolve,
          reject
        );
      }
    );

    if (stale.length === 0) return 0;

    for (const recording of stale) {
      recording.status = 'abandoned';
      try {
        await this.runTransaction<void>([STORE_RECORDINGS], 'readwrite', (tx, resolve, reject) => {
          const request = tx.objectStore(STORE_RECORDINGS).put(recording);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
        console.warn('[RecordingStorage] Recovered an abandoned recording:', recording.id);
      } catch (error) {
        console.error(`[RecordingStorage] Failed to promote ${recording.id} to abandoned:`, error);
      }
    }

    // One refresh for the whole batch rather than one per row.
    await this.refreshFailedRecordings().catch(err =>
      console.error('Failed to refresh after reconciling abandoned recordings:', err)
    );

    return stale.length;
  }

  // Get recordings for specific patient
  async getRecordingsByPatient(patientId: string): Promise<FailedRecording[]> {
    const recordings = await this.runTransaction<FailedRecording[]>(
      [STORE_RECORDINGS],
      'readonly',
      (tx, resolve, reject) => {
        const request = tx.objectStore(STORE_RECORDINGS).index('patientId').openCursor(IDBKeyRange.only(patientId));
        this.collectCursor<FailedRecording>(request, (record: FailedRecording) => record, resolve, reject);
      }
    );

    recordings.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return recordings;
  }

  // Get single recording by ID
  async getRecording(id: string): Promise<FailedRecording | null> {
    return this.runTransaction<FailedRecording | null>([STORE_RECORDINGS], 'readonly', (tx, resolve, reject) => {
      const request = tx.objectStore(STORE_RECORDINGS).get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all segments for a recording, audio payload included, ordered by `order`.
   *
   * Only retry and download need this — everything else should use
   * {@link getRecordingSegmentMetadata}, which does not load the audio.
   */
  async getRecordingSegments(recordingId: string): Promise<AudioSegment[]> {
    const segments = await this.runTransaction<AudioSegment[]>(
      [STORE_SEGMENTS],
      'readonly',
      (tx, resolve, reject) => {
        const request = tx.objectStore(STORE_SEGMENTS).index('recordingId').openCursor(IDBKeyRange.only(recordingId));
        this.collectCursor<AudioSegment>(request, (record: any) => this.restoreSegment(record), resolve, reject);
      }
    );

    segments.sort((a, b) => a.order - b.order);
    return segments;
  }

  /** Segments for a recording without their audio payload, ordered by `order`. */
  async getRecordingSegmentMetadata(recordingId: string): Promise<AudioSegmentMetadata[]> {
    const segments = await this.runTransaction<AudioSegmentMetadata[]>(
      [STORE_SEGMENTS],
      'readonly',
      (tx, resolve, reject) => {
        const request = tx.objectStore(STORE_SEGMENTS).index('recordingId').openCursor(IDBKeyRange.only(recordingId));
        this.collectCursor<AudioSegmentMetadata>(request, (record: any) => this.toSegmentMetadata(record), resolve, reject);
      }
    );

    segments.sort((a, b) => a.order - b.order);
    return segments;
  }

  /**
   * Metadata for every segment that failed upload, transcription or processing.
   *
   * This walks the whole segments store, so it deliberately never materialises audio: the
   * previous getAll() version pulled every recording's audio into a single response, which
   * is what made the browser drop the connection.
   */
  async getFailedSegmentMetadata(): Promise<AudioSegmentMetadata[]> {
    return this.runTransaction<AudioSegmentMetadata[]>([STORE_SEGMENTS], 'readonly', (tx, resolve, reject) => {
      const request = tx.objectStore(STORE_SEGMENTS).openCursor();
      this.collectCursor<AudioSegmentMetadata>(
        request,
        (record: any) => {
          const metadata = this.toSegmentMetadata(record);
          return this.isFailedSegmentRecord(metadata) ? metadata : null;
        },
        resolve,
        reject
      );
    });
  }

  // Get recordings that have failed segments but may not be marked as failed themselves
  // Also creates virtual recordings from orphaned segments (segments without parent recordings)
  async getRecordingsWithFailedSegments(): Promise<FailedRecording[]> {
    try {
      // Get all failed segments (metadata only - no audio is loaded here)
      const failedSegments = await this.getFailedSegmentMetadata();

      if (failedSegments.length === 0) {
        return [];
      }

      // Group segments by recordingId
      const segmentsByRecordingId = new Map<string, AudioSegmentMetadata[]>();
      for (const segment of failedSegments) {
        if (!segmentsByRecordingId.has(segment.recordingId)) {
          segmentsByRecordingId.set(segment.recordingId, []);
        }
        segmentsByRecordingId.get(segment.recordingId)!.push(segment);
      }

      // Get recordings for each unique recordingId
      const recordingPromises = Array.from(segmentsByRecordingId.keys()).map(async (recordingId) => {
        try {
          const recording = await this.getRecording(recordingId);
          return { recordingId, recording, segments: segmentsByRecordingId.get(recordingId)! };
        } catch (error) {
          console.warn(`Failed to get recording ${recordingId} for failed segments:`, error);
          return { recordingId, recording: null, segments: segmentsByRecordingId.get(recordingId)! };
        }
      });

      const results = await Promise.all(recordingPromises);

      // Separate recordings that exist from orphaned segments
      const existingRecordings: FailedRecording[] = [];
      const orphanedSegmentsByRecordingId = new Map<string, AudioSegmentMetadata[]>();

      for (const result of results) {
        if (result.recording) {
          existingRecordings.push(result.recording);
        } else {
          // Orphaned segments - group by recordingId to create virtual recordings
          orphanedSegmentsByRecordingId.set(result.recordingId, result.segments);
        }
      }

      // Create virtual recordings from orphaned segments
      const virtualRecordings: FailedRecording[] = [];
      for (const [recordingId, segments] of orphanedSegmentsByRecordingId.entries()) {
        // Use the first segment's metadata to create the virtual recording
        // (all segments in a recording should have the same metadata)
        const firstSegment = segments[0];

        // Only create virtual recording if segment has required metadata
        if (firstSegment.patientId && firstSegment.groupId && firstSegment.userId) {
          const estimatedDurationSeconds = this.estimateDurationSeconds(segments);

          // Get failed segment IDs
          const failedSegmentIds = segments
            .filter(seg => !seg.uploadSuccess || seg.transcribeFailed || !seg.processed)
            .map(seg => seg.id);

          // Determine status based on segment failures
          const hasUploadFailures = segments.some(seg => !seg.uploadSuccess);
          const hasTranscriptionFailures = segments.some(seg => seg.transcribeFailed);
          const allProcessed = segments.every(seg => seg.processed || seg.retryAttempts >= 5);

          const virtualRecording: FailedRecording = {
            id: recordingId,
            jobId: recordingId,
            sessionId: recordingId,
            patientId: firstSegment.patientId,
            patientName: firstSegment.patientName,
            groupId: firstSegment.groupId,
            userId: firstSegment.userId || '',
            type: firstSegment.type || 'general',
            segmentIds: segments.map(seg => seg.id),
            totalDuration: estimatedDurationSeconds,
            startTime: firstSegment.startTime,
            finishTime: firstSegment.finishTime,
            status: hasTranscriptionFailures || !allProcessed ? 'failed' : 'pending',
            uploadFailed: hasUploadFailures,
            transcriptionFailed: hasTranscriptionFailures,
            failedSegments: failedSegmentIds,
            createdAt: new Date().toISOString(), // Use current date (we don't have original creation time for orphaned segments)
            version: 1
          };

          virtualRecordings.push(virtualRecording);
          console.log(`[RecordingStorage] Created virtual recording from ${segments.length} orphaned segment(s):`, recordingId);
        } else {
          console.warn(`[RecordingStorage] Skipping orphaned segments for ${recordingId} - missing required metadata (patientId, groupId, or userId)`);
        }
      }

      return [...existingRecordings, ...virtualRecordings];
    } catch (error) {
      console.error('Error getting recordings with failed segments:', error);
      return [];
    }
  }

  // ---------------------------------------------------------------------------------------
  // Failed recordings observable
  // ---------------------------------------------------------------------------------------

  /**
   * Suspends the debounced background scan.
   *
   * The scan walks the whole segments store, and it is scheduled after every segment write —
   * i.e. once a minute during a consultation and once per transcript during a retry. Callers
   * that write segments in bulk pause it for the duration; the depth counter keeps nested
   * callers (a retry started while a recording is open) correct.
   */
  pauseFailedRecordingsRefresh(): void {
    this.refreshSuspendDepth++;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      this.refreshMissedWhileSuspended = true;
    }
  }

  /** Resumes the background scan, running one refresh if any were suppressed. */
  resumeFailedRecordingsRefresh(): void {
    if (this.refreshSuspendDepth === 0) {
      return;
    }
    this.refreshSuspendDepth--;
    if (this.refreshSuspendDepth === 0 && this.refreshMissedWhileSuspended) {
      this.refreshMissedWhileSuspended = false;
      this.refreshFailedRecordings().catch(err =>
        console.error('Failed to refresh failed recordings after resume:', err)
      );
    }
  }

  // Debounced refresh to avoid too many updates when segments are stored frequently
  private scheduleFailedRecordingsRefresh(): void {
    if (this.refreshSuspendDepth > 0) {
      this.refreshMissedWhileSuspended = true;
      return;
    }
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.refreshFailedRecordings().catch(err =>
        console.error('Failed to refresh failed recordings after segment storage:', err)
      );
    }, 1000); // Wait 1 second before refreshing
  }

  /**
   * Every recording that has not yet become a note: explicitly tracked rows, plus virtual ones
   * reconstructed from orphaned segments.
   *
   * The single source of this list. `FailedRecordingsDialogComponent.loadFailedRecordings()`
   * used to reimplement it line for line, so the two could — and did — drift.
   */
  async getAllUnprocessedRecordings(): Promise<FailedRecording[]> {
    // Rows we track directly.
    const failedRecordings = await this.getFailedRecordings();

    // Plus anything reconstructable from segments whose parent row is missing.
    const recordingsWithFailedSegments = await this.getRecordingsWithFailedSegments();

    const recordingsMap = new Map<string, FailedRecording>();

    // Reconstructed first...
    for (const recording of recordingsWithFailedSegments) {
      recordingsMap.set(recording.id, recording);
    }

    // ...so a real row always wins over a reconstruction of the same id.
    for (const recording of failedRecordings) {
      recordingsMap.set(recording.id, recording);
    }

    return Array.from(recordingsMap.values());
  }

  // Refresh failed recordings and emit to subscribers
  // Includes both explicitly failed recordings and recordings with failed segments (including orphaned segments)
  private async refreshFailedRecordings(): Promise<void> {
    try {
      this.failedRecordingsSubject.next(await this.getAllUnprocessedRecordings());
    } catch (error) {
      console.error('Failed to refresh failed recordings observable:', error);
      // Keep the last known list rather than blanking the warning badge on a transient
      // storage error.
    }
  }

  // ---------------------------------------------------------------------------------------
  // Storage usage
  // ---------------------------------------------------------------------------------------

  // Get storage usage (quota estimation and approximate size)
  async getStorageUsage(): Promise<{ used: number; available: number; quota: number; usagePercent: number }> {
    if (!isPlatformBrowser(this.platformId)) {
      return { used: 0, available: 0, quota: 0, usagePercent: 0 };
    }

    try {
      // Get quota estimate
      const estimate = await navigator.storage.estimate();
      const quota = estimate.quota || 0;
      const usage = estimate.usage || 0;
      const available = quota - usage;

      // Size of everything we hold, in one metadata-only pass over the segments store.
      const ourRecordingsSize = await this.getStoredSegmentsSize();

      // Calculate percentage based on our actual recordings vs quota
      // This ensures percentage updates correctly when we delete recordings
      const usagePercent = quota > 0 ? (ourRecordingsSize / quota) * 100 : 0;

      return {
        used: Math.round(ourRecordingsSize / 1024 / 1024), // Convert to MB (our recordings size)
        available: Math.round(available / 1024 / 1024), // Convert to MB
        quota: Math.round(quota / 1024 / 1024), // Convert to MB
        usagePercent: Math.round(usagePercent * 100) / 100 // Percentage based on our recordings size
      };
    } catch (error) {
      console.warn('Failed to get storage usage:', error);
      return { used: 0, available: 0, quota: 0, usagePercent: 0 };
    }
  }

  /** Total bytes of stored audio, without loading any of it. */
  private async getStoredSegmentsSize(): Promise<number> {
    const sizes = await this.runTransaction<number[]>([STORE_SEGMENTS], 'readonly', (tx, resolve, reject) => {
      const request = tx.objectStore(STORE_SEGMENTS).openCursor();
      this.collectCursor<number>(request, (record: any) => record.size || 0, resolve, reject);
    });

    return sizes.reduce((total, size) => total + size, 0);
  }

  // Check if storage quota is available (with buffer)
  async checkStorageQuota(requiredBytes: number): Promise<{ available: boolean; message?: string }> {
    const usage = await this.getStorageUsage();
    const requiredMB = requiredBytes / 1024 / 1024;
    const availableMB = usage.available;

    if (availableMB < requiredMB) {
      return {
        available: false,
        message: `Insufficient storage. Required: ${requiredMB.toFixed(2)}MB, Available: ${availableMB.toFixed(2)}MB`
      };
    }

    // Warn if using > 80% of quota
    if (usage.usagePercent > 80) {
      return {
        available: true,
        message: `Storage is ${usage.usagePercent.toFixed(1)}% full. Consider deleting old recordings.`
      };
    }

    return { available: true };
  }
}
